import React, { useContext, useState, useRef } from 'react';
import { UserContext } from '../../UserContext';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../ApiContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import FormActionButton from '../../components/FormActionButton';
import NoSlotsAvailableView from './NoSlotsAvailableView';
import Container from '../../components/Container';
import Heading2 from '../../components/Heading2';
import { ApiErrorAlert, ApiError } from '../../components/ApiErrorAlert';

const formatDate = (dateString: string): { day: string; monthDay: string } => {
  const date = new Date(dateString + 'T00:00:00'); // Explicitly set time to midnight UTC to avoid timezone issues
  const optionsDay: Intl.DateTimeFormatOptions = { weekday: 'long' };
  const optionsMonthDay: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
  return {
    day: date.toLocaleDateString(undefined, optionsDay),
    monthDay: date.toLocaleDateString(undefined, optionsMonthDay),
  };
};

// Function to get next 5 days starting from tomorrow
const getNext10Days = (): string[] => {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= 10; i++) {
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + i);
    dates.push(nextDate.toISOString().split('T')[0]);
  }
  return dates;
};

const SelectDayScreen: React.FC = () => {
  const userContext = useContext(UserContext);
  const api = useApi();
  const [date, setDate] = useState(userContext?.userData.date || getNext10Days()[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [showConciergeForm, setShowConciergeForm] = useState(false);
  const navigate = useNavigate();
  const dateContainerRef = useRef<HTMLDivElement>(null);

  if (!userContext) return null;

  const { userData, setUserData } = userContext;

  const handleNext = async () => {
    setUserData({ ...userData, date });
    setIsLoading(true);

    const { data, error } = await api.client.getAvailableSlots({
      zip: userData.zipcode,
      service_ids: userData.service_ids,
      date,
    });
    setIsLoading(false);

    if (error) {
      setApiError(error);
      return;
    } else if (!data || data.length === 0) {
      setShowConciergeForm(true);
      return;
    } else {
      navigate('/choose-time-slot', { state: { slots: data } });
    }
  };

  const next10Days = getNext10Days();

  const scrollLeft = () => {
    if (dateContainerRef.current) {
      dateContainerRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (dateContainerRef.current) {
      dateContainerRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  return (
    <Container>
      <Heading2>Choose Your Preferred Date</Heading2>
      <p>
        Based on your treatment selection and your preferred date, we'll search for providers within your area that have
        available times.
        <br />
        <br />
      </p>
      <div className="mb-6">
        <div className="flex items-center justify-center">
          <button className="p-2" onClick={scrollLeft}>
            <FontAwesomeIcon icon={faChevronLeft} className="h-6 w-6 text-gray-700" />
          </button>
          <div className="flex overflow-x-auto space-x-4 px-4" ref={dateContainerRef}>
            {next10Days.map((day, index) => {
              const { day: dayOfWeek, monthDay } = formatDate(day);
              return (
                <label key={index} className="flex flex-col items-center cursor-pointer min-w-max">
                  <input
                    type="radio"
                    value={day}
                    checked={date === day}
                    onChange={(e) => {
                      setShowConciergeForm(false);
                      setDate(e.target.value);
                    }}
                    className="hidden"
                  />
                  <div
                    className={`flex flex-col items-center border-2 rounded-lg px-2 py-1 ${
                      date === day
                        ? 'text-white border-[#1b1a30] bg-[#1b1a30]'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    <span className="whitespace-nowrap leading-5">{dayOfWeek}</span>
                    <span className="whitespace-nowrap leading-5">{monthDay}</span>
                  </div>
                </label>
              );
            })}
          </div>
          <button className="p-2" onClick={scrollRight}>
            <FontAwesomeIcon icon={faChevronRight} className="h-6 w-6 text-gray-700" />
          </button>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <FormActionButton onClick={handleNext} isLoading={isLoading}>
          Next
        </FormActionButton>
      </div>
      {showConciergeForm && (
        <div className="flex justify-center mt-12">
          <NoSlotsAvailableView />
        </div>
      )}
      <ApiErrorAlert screenName="select day screen" apiError={apiError} clearError={setApiError} />
    </Container>
  );
};

export default SelectDayScreen;
